<script lang="ts">
  import { mdiInformationOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { OrganizationPresentation } from "../../../../api/public/presentation"
  import {
    listContactMethods,
    requestContactMethodDeletion,
    setContactMethodChannelPermission,
    type PublicContactMethodChannel,
    type PublicContactMethodResponse,
    type PublicContactMethodsResponse,
  } from "../../../../api/public/appointment-portal"
  import ContactMethodDeleteDialog from "./_modules/contact-method-delete-dialog"
  import ContactMethodList from "./_modules/contact-method-list"
  import ContactMethodState from "./_modules/contact-method-state"

  const {
    organization,
    loading,
    error,
    load,
    orgIdentifier,
    contactStepCode,
    initialContactMethodsState = null,
  } = $props<{
    organization: OrganizationPresentation | null
    loading: boolean
    error: string | null
    load: () => Promise<void> | void
    orgIdentifier: string
    contactStepCode: string | null
    initialContactMethodsState?: PublicContactMethodsResponse | null
  }>()

  const KEHRBLATT_MESSAGING_URL = "https://kehrblatt.de/messaging"

  let contactMethodsState = $state<PublicContactMethodsResponse | null>(null)
  let contactMethodsLoading = $state(false)
  let contactMethodsError = $state<string | null>(null)
  let loadedKey = $state<string | null>(null)
  let appliedInitialKey = $state<string | null>(null)
  let loadRunId = 0
  let pendingChannelKey = $state<string | null>(null)
  let deleteDialogMethod = $state<PublicContactMethodResponse | null>(null)
  let deletePending = $state(false)
  let rowMessageById = $state<Record<string, string>>({})

  const organizationDisplayName = $derived(
    organization?.public_name?.trim() || organization?.name?.trim() || "den Betrieb",
  )
  const hasCode = $derived(Boolean(contactStepCode?.trim()))
  const visibleContactMethodsState = $derived(contactMethodsState ?? initialContactMethodsState)
  const contactMethods = $derived(visibleContactMethodsState?.contact_methods ?? [])
  const contactMethodsKey = $derived(
    hasCode && contactStepCode ? `${orgIdentifier}:${contactStepCode}` : null,
  )

  const replaceContactMethod = (nextMethod: PublicContactMethodResponse) => {
    const current = contactMethodsState

    if (!current) return

    contactMethodsState = {
      ...current,
      contact_methods: current.contact_methods.map((method) =>
        method.id === nextMethod.id ? nextMethod : method,
      ),
    }
  }

  const patchContactMethod = (
    contactPointId: string,
    patch: Partial<PublicContactMethodResponse>,
  ) => {
    const current = contactMethodsState

    if (!current) return

    contactMethodsState = {
      ...current,
      contact_methods: current.contact_methods.map((method) =>
        method.id === contactPointId ? { ...method, ...patch } : method,
      ),
    }
  }

  const setRowMessage = (contactPointId: string, message: string) => {
    rowMessageById = { ...rowMessageById, [contactPointId]: message }
  }

  const resetContactMethods = () => {
    contactMethodsState = null
    contactMethodsError = null
    loadedKey = null
    appliedInitialKey = null
    contactMethodsLoading = false
  }

  const loadContactMethods = async (options: { force?: boolean } = {}) => {
    if (!hasCode || !contactStepCode) {
      resetContactMethods()

      return
    }

    const key = contactMethodsKey

    if (!key) {
      resetContactMethods()

      return
    }

    if (!options.force && loadedKey === key) return

    const runId = ++loadRunId
    contactMethodsLoading = true
    contactMethodsError = null
    try {
      const response = await listContactMethods(orgIdentifier, contactStepCode)

      if (runId !== loadRunId) return

      contactMethodsState = response
      loadedKey = key
    } catch (err) {
      if (runId !== loadRunId) return

      console.error("contact_methods_load_failed", err)
      contactMethodsError = "Kontaktmittel konnten nicht geladen werden."
      contactMethodsState = null
      loadedKey = key
    } finally {
      if (runId === loadRunId) {
        contactMethodsLoading = false
      }
    }
  }

  const setChannelPermission = async (
    method: PublicContactMethodResponse,
    channel: PublicContactMethodChannel,
    isEnabled: boolean,
  ) => {
    if (!contactStepCode || pendingChannelKey) return

    const pendingKey = `${method.id}:${channel}`
    pendingChannelKey = pendingKey
    try {
      const nextMethod = await setContactMethodChannelPermission(
        orgIdentifier,
        contactStepCode,
        method.id,
        channel,
        isEnabled,
      )
      replaceContactMethod(nextMethod)
      setRowMessage(method.id, isEnabled ? "Kontaktweg aktiviert." : "Kontaktweg deaktiviert.")
    } catch (err) {
      console.error("contact_method_channel_permission_failed", err)
      setRowMessage(
        method.id,
        "Der Kontaktweg konnte nicht geändert werden. Bitte versuchen Sie es erneut oder wenden Sie sich direkt an den Betrieb.",
      )
    } finally {
      pendingChannelKey = null
    }
  }

  const requestDeletion = async () => {
    const method = deleteDialogMethod

    if (!method || !contactStepCode || deletePending) return

    deletePending = true
    try {
      const response = await requestContactMethodDeletion(orgIdentifier, contactStepCode, method.id)

      if (response.contact_method) {
        replaceContactMethod(response.contact_method)
      } else {
        patchContactMethod(method.id, {
          deletion_requested: true,
          channels: method.channels.map((channel) => ({
            ...channel,
            is_enabled: false,
            disabled_reason: channel.disabled_reason ?? "customer_delete_request",
          })),
        })
      }

      setRowMessage(method.id, "Löschung angefragt.")
      deleteDialogMethod = null
    } catch (err) {
      console.error("contact_method_delete_request_failed", err)
      setRowMessage(
        method.id,
        "Die Löschung konnte nicht angefragt werden. Bitte versuchen Sie es erneut.",
      )
    } finally {
      deletePending = false
    }
  }

  const openMessagingInformation = () => {
    window.open(KEHRBLATT_MESSAGING_URL, "_blank", "noopener,noreferrer")
  }

  $effect(() => {
    const key = contactMethodsKey

    if (!key || !initialContactMethodsState || appliedInitialKey === key) return

    contactMethodsState = initialContactMethodsState
    contactMethodsError = null
    contactMethodsLoading = false
    loadedKey = key
    appliedInitialKey = key
  })

  $effect(() => {
    const key = contactMethodsKey

    if (!key) {
      resetContactMethods()

      return
    }

    if (loadedKey === key || contactMethodsLoading) return

    void loadContactMethods()
  })
</script>

<section class="contact-methods-page" aria-label="Kontaktmittel">
  <SectionTitle title="Kontaktmittel" brightBackground={true} styleOuter="margin-bottom: 0;">
    {#snippet buttonSnippet()}
      <CTA
        type="tertiaryButton"
        icon={mdiInformationOutline}
        content="Hinweise"
        darkMode={false}
        onClick={openMessagingInformation}
      />
    {/snippet}
  </SectionTitle>
  <p>
    Hier können Sie prüfen, über welche Kontaktmittel wir Sie zur Terminabstimmung erreichen. Sie
    können einzelne Kontaktwege abbestellen oder eine Löschung gespeicherter Kontaktdaten anfragen.
  </p>
  {#if loading}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState message="Kontaktmittel werden vorbereitet…" variant="loading" />
    </ActionBox>
  {:else if error}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState
        message={error}
        variant="error"
        retryLabel="Erneut laden"
        onRetry={load}
      />
    </ActionBox>
  {:else if !hasCode}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState
        message="Kontaktmittel können nur über einen persönlichen Terminlink angezeigt werden."
      />
    </ActionBox>
  {:else if contactMethodsLoading}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState message="Kontaktmittel werden geladen…" variant="loading" />
    </ActionBox>
  {:else if contactMethodsError}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState
        message={contactMethodsError}
        variant="error"
        retryLabel="Erneut laden"
        onRetry={() => loadContactMethods({ force: true })}
      />
    </ActionBox>
  {:else if visibleContactMethodsState && !visibleContactMethodsState.available}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState
        message="Diese Kontaktmittel können mit diesem Link nicht angezeigt werden."
      />
    </ActionBox>
  {:else if !contactMethods.length}
    <ActionBox label="Gespeicherte Kontaktmittel" darkMode={true}>
      <ContactMethodState
        message="Für diesen Ansprechpartner sind keine Kontaktmittel gespeichert."
      />
    </ActionBox>
  {:else}
    <ContactMethodList
      {contactMethods}
      {pendingChannelKey}
      {rowMessageById}
      onSetChannelPermission={setChannelPermission}
      onRequestDeletion={(method) => {
        deleteDialogMethod = method
      }}
    />
  {/if}
</section>

{#if deleteDialogMethod}
  <ContactMethodDeleteDialog
    {organizationDisplayName}
    pending={deletePending}
    onCancel={() => {
      if (!deletePending) deleteDialogMethod = null
    }}
    onConfirm={requestDeletion}
  />
{/if}

<style lang="less">
  .contact-methods-page {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    p {
      margin: 0;
    }
  }
</style>
